'use client';

import { useEffect } from 'react';
import { clientLogger } from '@/lib/client-logger';
const log = clientLogger.child('error-boundary');
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePathname } from 'next/navigation';
import Link from 'next/link';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();

  const getDashboardLink = () => {
    if (pathname?.startsWith('/customer')) return '/customer/dashboard';
    if (pathname?.startsWith('/technician')) return '/technician/today';
    return '/dashboard';
  };

  useEffect(() => {
    // Log the error to console in development
    if (process.env.NODE_ENV === 'development') {
      log.error('Application error', { err: error });
    }
    
    // You could also send to error reporting service here
    // Example: Sentry.captureException(error);
  }, [error]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {/* Error Illustration */}
        <div className="mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-32 h-32 bg-red-100 rounded-full flex items-center justify-center animate-pulse">
              <AlertTriangle className="h-16 w-16 text-red-600" />
            </div>
          </div>
        </div>

        {/* Error Message */}
        <h1 className="text-3xl font-bold text-slate-900 mb-3">
          Something Went Wrong
        </h1>
        <p className="text-slate-600 mb-8">
          We encountered an unexpected error. Don&apos;t worry, our team has been notified 
          and is working on fixing it.
        </p>

        {/* Error Details (Development Only) */}
        {process.env.NODE_ENV === 'development' && error.message && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
            <p className="text-sm font-mono text-red-800">
              {error.message}
            </p>
            {error.digest && (
              <p className="text-xs text-red-600 mt-2">
                Error ID: {error.digest}
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button 
            onClick={reset}
            className="w-full"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
          
          <Link href={getDashboardLink()} className="block">
            <button className="w-full px-4 py-2 border-2 border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center justify-center">
              <Home className="mr-2 h-4 w-4" />
              Go to Dashboard
            </button>
          </Link>
        </div>

        {/* Help Text */}
        <div className="mt-8 pt-8 border-t border-slate-200">
          <p className="text-sm text-slate-500">
            If this problem persists, please{' '}
            <Link href="/settings" className="text-cyan-600 hover:text-cyan-700 font-medium">
              contact support
            </Link>
            {' '}with error ID: {error.digest || 'unknown'}
          </p>
        </div>
      </div>
    </div>
  );
}
